/**
 * レンタル期間の延長ページ（sections/wifi-extend.liquid → /pages/extend）
 *
 * 1) 注文番号＋メールアドレスで POST /extension/lookup
 * 2) 返されたモードに応じて日付を選ばせ、料金を表示
 *      extend  : 延長日数を30日/90日プラン込みで最安計算
 *      overdue : 1日プラン × 超過日数（利用規約 第4条）
 *      contact / unavailable : Webでは手続き不可の案内
 * 3) 延長商品（rental-extension）をカートに入れて /checkout へ
 *
 * 返却日の書き換えは決済後に order-handler の Webhook が行う。ここでは何も書き換えない。
 * 料金計算は lambdas/order-handler/extension.mjs の planForDays と同じ考え方にそろえること。
 */
(function () {
  'use strict';

  const root = document.getElementById('wifi-extend');
  if (!root) return;

  const $ = (id) => document.getElementById(id);
  const show = (el, on = true) => { if (el) el.hidden = !on; };
  const apiBase = String(root.dataset.apiBase || '').replace(/\/$/, '');

  let variants = [];
  try { variants = JSON.parse($('wifi-ext-variants').textContent || '[]'); } catch (_) { variants = []; }

  const PLAN_LABEL = { 1: '1日プラン', 30: '30日プラン', 90: '90日プラン' };
  const yen = (n) => `¥${Number(n).toLocaleString('ja-JP')}`;

  function utc(s) { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); }
  function diffDays(from, to) { return Math.round((utc(to) - utc(from)) / 86400000); }
  function jaDate(s) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(s || ''))) return '—';
    const [y, m, d] = s.split('-').map(Number);
    const w = '日月火水木金土'[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
    return `${m}月${d}日（${w}）`;
  }
  function shortDate(s) { const [, m, d] = s.split('-').map(Number); return `${m}/${d}`; }

  function planForDays(days, prices, pricing) {
    if (!(days >= 1) || !Number.isFinite(prices[1])) return null;
    if (pricing === 'daily') return { plan90: 0, plan30: 0, plan1: days, cost: days * prices[1] };
    const term = (n, d) => (n ? n * (Number.isFinite(prices[d]) ? prices[d] : Infinity) : 0);
    let best = null;
    for (let n90 = 0; n90 <= Math.ceil(days / 90); n90++) {
      const after90 = Math.max(0, days - n90 * 90);
      for (let n30 = 0; n30 <= Math.ceil(after90 / 30); n30++) {
        const n1 = Math.max(0, after90 - n30 * 30);
        const cost = term(n90, 90) + term(n30, 30) + term(n1, 1);
        if (!Number.isFinite(cost)) continue;
        if (!best || cost < best.cost) best = { plan90: n90, plan30: n30, plan1: n1, cost };
      }
    }
    return best;
  }

  // Shopifyのカートは連続で叩くと 429 を返すので、申し込みフォームと同じく少し待って再送する
  async function postCart(path, body, attempt = 0) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 429 && attempt < 2) {
      const wait = Math.min(5, Number(res.headers.get('Retry-After')) || 1) * 1000;
      await new Promise((r) => setTimeout(r, wait));
      return postCart(path, body, attempt + 1);
    }
    if (!res.ok) throw new Error(`cart ${path} HTTP ${res.status}`);
    return res.json();
  }

  function setError(id, msg) {
    const el = $(id);
    if (!el) return;
    el.textContent = msg || '';
    show(el, !!msg);
  }

  /** 機種名に対応する延長商品のバリアント（{1:{id,price},30:…,90:…}） */
  function pricesFor(deviceTitle) {
    const p = {};
    for (const v of variants) {
      if (v.device !== deviceTitle) continue;
      const m = String(v.plan).match(/(\d+)日/);
      const d = m ? Number(m[1]) : 0;
      if (d === 1 || d === 30 || d === 90) p[d] = { id: v.id, price: Number(v.price) };
    }
    return p;
  }

  let ctx = null;   // 照会結果
  let quote = null; // 選んだ日付の見積もり

  // ── 1. 照会 ────────────────────────────────────────────────

  const params = new URLSearchParams(window.location.search);
  const presetOrder = params.get('order');
  if (presetOrder) $('wifi-ext-order').value = presetOrder.startsWith('#') ? presetOrder : `#${presetOrder}`;

  $('wifi-ext-lookup').addEventListener('submit', async (e) => {
    e.preventDefault();
    setError('wifi-ext-lookup-error', '');
    const orderNumber = $('wifi-ext-order').value.trim();
    const email = $('wifi-ext-email').value.trim();
    if (!orderNumber || !email) {
      setError('wifi-ext-lookup-error', 'ご注文番号とメールアドレスを入力してください。');
      return;
    }
    const btn = $('wifi-ext-lookup-btn');
    btn.disabled = true;
    btn.textContent = '確認しています…';
    try {
      const res = await fetch(`${apiBase}/extension/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber, email }),
      });
      if (res.status === 404) {
        setError('wifi-ext-lookup-error', 'ご注文が見つかりませんでした。ご注文番号と、ご注文時のメールアドレスをご確認ください。');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      renderOrder(await res.json());
    } catch (_) {
      setError('wifi-ext-lookup-error', '通信に失敗しました。時間をおいて再度お試しいただくか、お問い合わせください。');
    } finally {
      btn.disabled = false;
      btn.textContent = 'ご注文を確認する';
    }
  });

  // ── 2. 注文の表示とモード別の案内 ─────────────────────────

  function renderOrder(data) {
    ctx = data;
    quote = null;
    const o = data.order;

    const dl = $('wifi-ext-order-summary');
    dl.textContent = '';
    for (const [k, v] of [
      ['ご注文番号', o.orderNumber],
      ['機種', `${o.deviceTitle || '—'}（${o.deviceCount}台）`],
      ['ご利用開始日', jaDate(o.startDate)],
      ['現在のご返却日', `${jaDate(o.endDate)}${o.extensionCount ? `（延長${o.extensionCount}回）` : ''}`],
    ]) {
      const dt = document.createElement('dt');
      dt.textContent = k;
      const dd = document.createElement('dd');
      dd.textContent = v;
      dl.append(dt, dd);
    }

    const notice = $('wifi-ext-notice');
    notice.className = 'wifi-ext__notice';
    show(notice, false);
    show($('wifi-ext-date-area'), false);
    show($('wifi-ext-quote'), false);
    setError('wifi-ext-checkout-error', '');
    const say = (cls, text) => { notice.classList.add(cls); notice.textContent = text; show(notice, true); };

    ctx.prices = pricesFor(o.deviceTitle);

    if (data.mode === 'extend' || data.mode === 'overdue') {
      if (!ctx.prices[1]) {
        say('wifi-ext__notice--warn', 'この機種の延長はWebでお手続きいただけません。お手数ですがお問い合わせください。');
      } else {
        const input = $('wifi-ext-date');
        input.value = '';
        input.min = data.minNewEndDate;
        input.max = data.maxNewEndDate;
        const range = `${jaDate(data.minNewEndDate)}〜${jaDate(data.maxNewEndDate)}の間で選べます`;
        if (data.mode === 'overdue') {
          say('wifi-ext__notice--warn', `ご返却日を${data.overdueDays}日過ぎています。超過日数分の料金（1日プラン相当額）のお支払いをお願いいたします。ポストへ投函する予定の日を選んでください。`);
          $('wifi-ext-date-label').textContent = '投函予定日（この日までにポストへ投函してください）';
          $('wifi-ext-date-hint').textContent = `${range}。`;
          $('wifi-ext-checkout').textContent = '超過料金のお支払いへ進む';
        } else {
          $('wifi-ext-date-label').textContent = '新しいご返却日（ポスト投函日）';
          $('wifi-ext-date-hint').textContent = `${range}（ご利用開始日から通算${data.rules.maxTotalDays}日まで）。`;
          $('wifi-ext-checkout').textContent = '延長料金のお支払いへ進む';
        }
        $('wifi-ext-checkout').disabled = true;
        show($('wifi-ext-date-area'), true);
      }
    } else if (data.mode === 'contact') {
      say('wifi-ext__notice--warn', data.reason === 'max_total'
        ? `ご利用開始日から通算${data.rules.maxTotalDays}日を超える延長は、Webではお手続きいただけません。お手数ですがお問い合わせください。`
        : `ご返却日から${data.rules.overdueLimitDays}日以上過ぎているため、Webではお手続きいただけません。至急お問い合わせください。`);
    } else {
      say('wifi-ext__notice--info', 'このご注文は延長のお手続きの対象外です（ご返却済み・キャンセル等）。ご不明な点はお問い合わせください。');
    }

    show($('wifi-ext-step-lookup'), false);
    show($('wifi-ext-step-order'), true);
    $('wifi-ext-step-order').scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  $('wifi-ext-back').addEventListener('click', () => {
    ctx = null;
    quote = null;
    show($('wifi-ext-step-order'), false);
    show($('wifi-ext-step-lookup'), true);
  });

  // ── 3. 日付を選んだら見積もり ─────────────────────────────

  $('wifi-ext-date').addEventListener('change', () => {
    const v = $('wifi-ext-date').value;
    quote = null;
    show($('wifi-ext-quote'), false);
    $('wifi-ext-checkout').disabled = true;
    setError('wifi-ext-checkout-error', '');
    if (!v || !ctx) return;

    // カレンダーが使えないブラウザでは範囲外を手入力できてしまうため、ここでも確認する
    if (v < ctx.minNewEndDate || v > ctx.maxNewEndDate) {
      setError('wifi-ext-checkout-error', `${jaDate(ctx.minNewEndDate)}〜${jaDate(ctx.maxNewEndDate)}の間で選んでください。`);
      return;
    }

    const o = ctx.order;
    const days = diffDays(o.endDate, v);
    const unit = { 1: ctx.prices[1] && ctx.prices[1].price, 30: ctx.prices[30] && ctx.prices[30].price, 90: ctx.prices[90] && ctx.prices[90].price };
    const plan = planForDays(days, unit, ctx.pricing);
    if (!plan) {
      setError('wifi-ext-checkout-error', '料金を計算できませんでした。お問い合わせください。');
      return;
    }
    const units = o.deviceCount;
    quote = { to: v, days, plan, total: plan.cost * units };

    const box = $('wifi-ext-quote');
    box.textContent = '';
    const row = (label, value, cls) => {
      const d = document.createElement('div');
      d.className = `wifi-ext__row${cls ? ` ${cls}` : ''}`;
      const a = document.createElement('span');
      a.textContent = label;
      const b = document.createElement('span');
      b.textContent = value;
      d.append(a, b);
      box.append(d);
    };
    row(`${ctx.mode === 'overdue' ? '超過' : '延長'}日数`, `${days}日（${jaDate(o.endDate)} → ${jaDate(v)}）`);
    const suffix = units > 1 ? ` × ${units}台` : '';
    for (const d of [90, 30, 1]) {
      const n = plan[`plan${d}`];
      if (n > 0) row(`${PLAN_LABEL[d]} × ${n}${suffix}`, yen(n * unit[d] * units));
    }
    row('お支払い金額（税込）', yen(quote.total), 'wifi-ext__row--total');

    if (ctx.mode === 'extend') {
      const daily = days * unit[1] * units;
      if (daily > quote.total) {
        const s = document.createElement('p');
        s.className = 'wifi-ext__saving';
        s.textContent = `30日・90日プランを適用しました（${yen(daily - quote.total)}お得になりました）`;
        box.append(s);
      }
    }
    const note = document.createElement('p');
    note.className = 'wifi-ext__hint';
    note.textContent = '配送料はかかりません。';
    box.append(note);

    show(box, true);
    $('wifi-ext-checkout').disabled = false;
  });

  // ── 4. カートに入れて決済へ ──────────────────────────────

  $('wifi-ext-checkout').addEventListener('click', async () => {
    if (!ctx || !quote) return;
    const btn = $('wifi-ext-checkout');
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = '準備しています…';
    setError('wifi-ext-checkout-error', '');

    const o = ctx.order;
    const units = o.deviceCount;
    // _ で始まる項目はお客様には見えず、Webhook が読む。見える2項目は決済画面での確認用。
    const properties = {
      _category: 'postal-wifi',
      _type: 'extension',
      _extendOrderId: o.orderId,
      _extendOrderNumber: o.orderNumber,
      _extendFrom: o.endDate,
      _extendTo: quote.to,
      _extendMode: ctx.mode,
      _extendDays: String(quote.days),
      _deviceCount: String(units),
      'ご注文番号': o.orderNumber,
      'ご返却日の変更': `${shortDate(o.endDate)} → ${shortDate(quote.to)}`,
    };
    const items = [];
    for (const d of [90, 30, 1]) {
      const n = quote.plan[`plan${d}`];
      if (n > 0) items.push({ id: ctx.prices[d].id, quantity: n * units, properties });
    }

    try {
      // 他の商品が入ったまま決済されると延長として自動処理できないため、空にしてから入れる
      await postCart('/cart/clear.js');
      await postCart('/cart/add.js', { items });
      window.location.href = '/checkout';
    } catch (_) {
      setError('wifi-ext-checkout-error', 'お手続きを開始できませんでした。時間をおいて再度お試しください。');
      btn.disabled = false;
      btn.textContent = label;
    }
  });
})();
